import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Badge,
  Button,
  Drawer,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconCalendarStats, IconEdit, IconLock, IconLockOpen, IconPlus, IconReportMoney, IconTrash } from '@tabler/icons-react';
import { isAxiosError } from 'axios';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import {
  useAccountingPeriods,
  useCreatePeriod,
  useDeletePeriod,
  usePostClosingVoucher,
  useSetPeriodClosed,
  useUpdatePeriod,
  type AccountingPeriodDto,
} from './periodsApi';

const toIso = (d: Date | null) => (d ? d.toISOString().slice(0, 10) : '');

export function AccountingPeriodsPage() {
  const { t, i18n } = useTranslation();
  const { data, isLoading } = useAccountingPeriods();
  const create = useCreatePeriod();
  const update = useUpdatePeriod();
  const setClosed = useSetPeriodClosed();
  const del = useDeletePeriod();
  const voucher = usePostClosingVoucher();

  const [drawer, setDrawer] = useState(false);
  const [editing, setEditing] = useState<AccountingPeriodDto | null>(null);
  const [deleting, setDeleting] = useState<AccountingPeriodDto | null>(null);
  const [closingYear, setClosingYear] = useState<AccountingPeriodDto | null>(null);

  const form = useForm({
    initialValues: { nameAr: '', nameEn: '', start: null as Date | null, end: null as Date | null },
    validate: {
      nameAr: (v) => (v.trim() ? null : t('common.errors.required')),
      start: (v) => (v ? null : t('common.errors.required')),
      end: (v, values) => (v && values.start && v >= values.start ? null : t('accounting.periods.badRange')),
    },
  });

  const openCreate = () => {
    setEditing(null);
    form.setValues({ nameAr: '', nameEn: '', start: null, end: null });
    form.resetDirty();
    setDrawer(true);
  };
  const openEdit = (p: AccountingPeriodDto) => {
    setEditing(p);
    form.setValues({ nameAr: p.nameAr, nameEn: p.nameEn ?? '', start: new Date(p.startDate), end: new Date(p.endDate) });
    form.resetDirty();
    setDrawer(true);
  };

  const submit = form.onSubmit(async (v) => {
    const req = { nameAr: v.nameAr.trim(), nameEn: v.nameEn.trim() || null, startDate: toIso(v.start), endDate: toIso(v.end) };
    try {
      if (editing) await update.mutateAsync({ id: editing.accountingPeriodId, req });
      else await create.mutateAsync(req);
      notifications.show({ color: 'green', title: t('common.toasts.saved'), message: v.nameAr });
      setDrawer(false);
    } catch (err) {
      notifications.show({
        color: 'red', title: t('common.toasts.errorTitle'),
        message: (isAxiosError(err) && (err.response?.data?.detail as string)) || t('common.toasts.errorBody'),
      });
    }
  });

  const toggleClosed = async (p: AccountingPeriodDto) => {
    try {
      await setClosed.mutateAsync({ id: p.accountingPeriodId, closed: !p.isClosed });
      notifications.show({ color: 'green', title: p.isClosed ? t('accounting.periods.reopened') : t('accounting.periods.closed'), message: p.nameAr });
    } catch {
      notifications.show({ color: 'red', title: t('common.toasts.errorTitle'), message: t('common.toasts.errorBody') });
    }
  };

  const rows = data ?? [];

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.accounting')}
        title={t('accounting.periods.title')}
        subtitle={t('accounting.periods.subtitle')}
        actions={
          <Button leftSection={<IconPlus size={16} stroke={1.8} />} onClick={openCreate}>
            {t('accounting.periods.create')}
          </Button>
        }
      />

      <Paper p={rows.length ? 0 : 'lg'}>
        {isLoading ? (
          <Stack align="center" py={48}><Loader size="sm" /></Stack>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<IconCalendarStats size={22} stroke={1.6} />}
            title={t('accounting.periods.empty')}
            description={t('accounting.periods.emptyBody')}
            actionLabel={t('accounting.periods.create')}
            onAction={openCreate}
          />
        ) : (
          <Table verticalSpacing="md" highlightOnHover striped="even">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('accounting.periods.name')}</Table.Th>
                <Table.Th>{t('accounting.periods.range')}</Table.Th>
                <Table.Th>{t('accounting.periods.status')}</Table.Th>
                <Table.Th style={{ width: 140, textAlign: 'end' }}>{t('common.actions')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((p) => (
                <Table.Tr key={p.accountingPeriodId}>
                  <Table.Td>
                    <Text fw={500} c="var(--wheels-ink-strong)">
                      {i18n.language === 'ar' ? p.nameAr : (p.nameEn ?? p.nameAr)}
                    </Text>
                  </Table.Td>
                  <Table.Td className="font-numeric" c="var(--wheels-ink-muted)">
                    {p.startDate} → {p.endDate}
                  </Table.Td>
                  <Table.Td>
                    {p.isClosed ? (
                      <Badge color="red" variant="light" radius="sm" leftSection={<IconLock size={12} />}>
                        {t('accounting.periods.closedBadge')}
                      </Badge>
                    ) : (
                      <Badge color="green" variant="light" radius="sm" leftSection={<IconLockOpen size={12} />}>
                        {t('accounting.periods.openBadge')}
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                      {!p.isClosed && (
                        <Tooltip label={t('accounting.periods.closeYear')} withinPortal>
                          <ActionIcon variant="subtle" color="wheels" onClick={() => setClosingYear(p)} aria-label={t('accounting.periods.closeYear')}>
                            <IconReportMoney size={16} stroke={1.6} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      <Tooltip label={p.isClosed ? t('accounting.periods.reopen') : t('accounting.periods.close')} withinPortal>
                        <ActionIcon variant="subtle" color={p.isClosed ? 'green' : 'orange'}
                          onClick={() => toggleClosed(p)} aria-label="toggle">
                          {p.isClosed ? <IconLockOpen size={16} stroke={1.6} /> : <IconLock size={16} stroke={1.6} />}
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t('common.edit')} withinPortal>
                        <ActionIcon variant="subtle" color="gray" disabled={p.isClosed}
                          onClick={() => openEdit(p)} aria-label={t('common.edit')}>
                          <IconEdit size={16} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t('common.delete')} withinPortal>
                        <ActionIcon variant="subtle" color="red"
                          onClick={() => setDeleting(p)} aria-label={t('common.delete')}>
                          <IconTrash size={16} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <Drawer
        opened={drawer}
        onClose={() => setDrawer(false)}
        position={i18n.language === 'ar' ? 'left' : 'right'}
        size="md"
        padding="lg"
        title={editing ? t('accounting.periods.editTitle') : t('accounting.periods.create')}
      >
        <form onSubmit={submit}>
          <Stack gap="md">
            <TextInput label={t('accounting.periods.nameAr')} withAsterisk {...form.getInputProps('nameAr')} />
            <TextInput label={t('accounting.periods.nameEn')} {...form.getInputProps('nameEn')} />
            <DatePickerInput label={t('accounting.periods.startDate')} withAsterisk valueFormat="YYYY-MM-DD" {...form.getInputProps('start')} />
            <DatePickerInput label={t('accounting.periods.endDate')} withAsterisk valueFormat="YYYY-MM-DD" {...form.getInputProps('end')} />
            <Group justify="flex-end" gap="sm" mt="sm">
              <Button variant="default" onClick={() => setDrawer(false)}>{t('common.cancel')}</Button>
              <Button type="submit" loading={create.isPending || update.isPending}>
                {editing ? t('common.save') : t('common.create')}
              </Button>
            </Group>
          </Stack>
        </form>
      </Drawer>

      <ConfirmDialog
        opened={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          await del.mutateAsync(deleting.accountingPeriodId);
          setDeleting(null);
        }}
        title={t('accounting.periods.deleteTitle')}
        message={t('accounting.periods.deleteBody', { name: deleting?.nameAr ?? '' })}
        loading={del.isPending}
      />

      <ConfirmDialog
        opened={closingYear !== null}
        onClose={() => setClosingYear(null)}
        onConfirm={async () => {
          if (!closingYear) return;
          try {
            const res = await voucher.mutateAsync(closingYear.accountingPeriodId);
            notifications.show({
              color: 'green',
              title: t('accounting.periods.closedYear'),
              message: res.posted
                ? t('accounting.periods.voucherPosted', { entry: res.entryNumber, net: res.netIncome.toFixed(2) })
                : t('accounting.periods.noMovement'),
            });
            setClosingYear(null);
          } catch (err) {
            notifications.show({
              color: 'red', title: t('common.toasts.errorTitle'),
              message: (isAxiosError(err) && (err.response?.data?.detail as string)) || t('common.toasts.errorBody'),
            });
          }
        }}
        title={t('accounting.periods.closeYear')}
        message={t('accounting.periods.closeYearBody', { name: closingYear?.nameAr ?? '' })}
        loading={voucher.isPending}
      />
    </Stack>
  );
}
